import number from '~/utils/number'
import { useFantasyDayStore } from '@/stores/fantasy/day'
import { useFantasySeasonStore } from '@/stores/fantasy/season'
import type { StatusKey } from '../types'

export const useFantasyDayStatus = () => {
  const { t } = useI18n()
  const fantasyDayStore = useFantasyDayStore()

  const {
    isLocked,
    isCompleted,
    points,
    rewards,
    positions,
    slots,
    winnersCount,
    isParticipated
  } = storeToRefs(fantasyDayStore)
  const { isCompleted: isSeasonCompleted } = storeToRefs(
    useFantasySeasonStore()
  )

  const slotsCount = computed(() => slots.value.length)
  const positionsCount = computed(() => positions.value.length)

  const statusKey = computed<StatusKey>(() => {
    if (isCompleted.value) return 'completed'
    if (isLocked.value) return 'inProgress'
    if (slotsCount.value === positionsCount.value) return 'full'
    if (slotsCount.value === 0) return 'empty'
    return 'partial'
  })

  const statusText = computed(() => {
    const map: Record<StatusKey, string> = {
      empty: t('status.notSelected'),
      partial: t('status.partial'),
      full: t('status.full'),
      inProgress: t('status.inProgress'),
      completed: t('status.completed')
    }
    return map[statusKey.value] || ''
  })

  const statusIcon = computed(() => {
    const map: Record<StatusKey, string> = {
      empty: 'swords',
      partial: 'alert-triangle-outline',
      full: 'check',
      inProgress: 'lock-circled',
      completed: 'calendar'
    }
    return map[statusKey.value] || ''
  })

  const showLockTimer = computed(
    () => !isLocked.value && !isCompleted.value && !isSeasonCompleted.value
  )

  const completedPointsText = computed(() => {
    if (!isCompleted.value || !points.value) return ''
    return `+${number(points.value)}`
  })

  const subtext = computed(() => {
    if (isCompleted.value) {
      return isParticipated.value ? '' : t('subtext.notParticipated')
    }

    if (isLocked.value) {
      return t('subtext.dayStarted')
    }

    return ''
  })

  const rewardsTitle = computed(() =>
    isCompleted.value
      ? t('rewards.winnersCount', winnersCount.value)
      : t('rewards.title')
  )

  const rewardsSub = computed(() =>
    isCompleted.value ? t('rewards.delivered') : t('rewards.sub')
  )

  return {
    rewards,
    statusKey,
    statusText,
    statusIcon,
    showLockTimer,
    completedPointsText,
    subtext,
    rewardsTitle,
    rewardsSub
  }
}
